import React, { useEffect, useState } from 'react';

import {
  MemberEngagementLevel,
  ProgramTaskStatus,
  States,
} from '@vestahealthcare/common/enums';
import Enum, { Selectable } from '@vestahealthcare/common/enums/Enum';
import { translate } from '@vestahealthcare/common/i18n';
import { PodRule } from '@vestahealthcare/common/models';

import { Panel, Select, SelectLanguage } from 'styleguide-v2';
import { FilterGroup, FilterItem } from 'styleguide-v2/src/components/Panel';

import {
  FiltersVestaTeam,
  VestaTeamKey,
} from 'dash/src/components/filters/FiltersVestaTeam';
import { getFilterCount, getFilterItem } from 'dash/src/utils/filterUtils';

import { GetProgramTaskParam } from '../../../services/PodServices';

type KeyGetProgramTaskParams = keyof GetProgramTaskParam;

type Props = {
  data: {
    employees: Selectable[];
    languages: Selectable[];
    taskDefinitions: Selectable[];
    pods: Selectable[];
  };
  filters?: {
    [x in KeyGetProgramTaskParams]?: FilterItem;
  };
  loadingFilters?: boolean;
  open: boolean;
  onChange: (
    filters: {
      [x in KeyGetProgramTaskParams]?: FilterItem;
    },
  ) => void;
  onClose: () => void;
};

const MEMBER_FILTERS: KeyGetProgramTaskParams[] = ['state', 'language'];

const TASK_FILTERS: KeyGetProgramTaskParams[] = [
  'statusId',
  'taskDefinitionId',
  'childTaskDefinitionId',
  'assigneeId',
];

const VESTA_TEAM_FILTERS: VestaTeamKey[] = ['podId'];

const OPS_FILTERS: KeyGetProgramTaskParams[] = ['engagementLevel'];

type FilterType = {
  [x in KeyGetProgramTaskParams]?: FilterItem;
};

export const ProgramTaskManagementFilterModal = ({
  data,
  filters,
  loadingFilters,
  open,
  onChange,
  onClose,
}: Props) => {
  const [pendingFilters, setPendingFilters] = useState<FilterType>(
    filters || {},
  );

  useEffect(() => {
    setPendingFilters(filters || {});
  }, [filters]);

  return (
    <Panel.FilterModal
      open={open}
      onClear={() => {
        onChange({});
      }}
      onClose={onClose}
      onSubmit={() => onChange(pendingFilters)}
      size={400}
    >
      <FilterGroup
        title={translate('components.FilterGroup.member')}
        count={getFilterCount(pendingFilters, MEMBER_FILTERS)}
      >
        <SelectLanguage
          data-cy="program-tasks-modal-language"
          items={data.languages}
          label={translate('programTasksManagement.filter.language')}
          limitTags={1}
          loading={loadingFilters}
          onChange={(values: Selectable[]) => {
            setPendingFilters({
              ...pendingFilters,
              language: getFilterItem(
                translate('programTasksManagement.filter.language'),
                values,
              ),
            });
          }}
          size="small"
          value={(pendingFilters.language?.value as Selectable[]) || []}
        />
        <Select
          data-cy="program-tasks-modal-state"
          items={Enum.toSelectable(States.asArray)}
          label={translate('programTasksManagement.filter.state')}
          limitTags={1}
          multiple
          onChange={(values: Selectable[]) => {
            setPendingFilters({
              ...pendingFilters,
              state: getFilterItem(
                translate('programTasksManagement.filter.state'),
                values,
              ),
            });
          }}
          size="small"
          value={pendingFilters.state?.value || []}
        />
      </FilterGroup>
      <FiltersVestaTeam
        data={{ employees: data.employees, pods: data.pods }}
        filters={pendingFilters}
        keys={VESTA_TEAM_FILTERS}
        loading={loadingFilters}
        onChange={(updatedFilters) =>
          setPendingFilters({ ...pendingFilters, ...updatedFilters })
        }
      />
      <FilterGroup
        title={translate('components.FilterGroup.task')}
        count={getFilterCount(pendingFilters, TASK_FILTERS)}
      >
        <Select
          data-cy="program-task-management-filter-bar-status"
          items={Enum.toSelectable(ProgramTaskStatus.asArray).sort(Enum.sort)}
          label={translate('programTasksManagement.filter.status')}
          limitTags={1}
          multiple
          onChange={(values: Selectable[]) =>
            setPendingFilters({
              ...filters,
              statusId: getFilterItem(
                translate('programTasksManagement.filter.status'),
                values,
              ),
            })
          }
          size="small"
          value={filters?.statusId?.value || []}
        />
        <Select
          data-cy="program-task-management-filter-bar-type"
          items={data.taskDefinitions.filter(
            ({ value }) => !PodRule.CHILD_TYPES.includes(value as number),
          )}
          label={translate('programTasksManagement.filter.type')}
          limitTags={1}
          loading={loadingFilters}
          multiple
          onChange={(values: Selectable[]) =>
            onChange({
              ...filters,
              taskDefinitionId: getFilterItem(
                translate('programTasksManagement.filter.type'),
                values,
              ),
            })
          }
          size="small"
          value={filters?.taskDefinitionId?.value || []}
        />
        <Select
          data-cy="program-task-management-filter-bar-subtype"
          items={data.taskDefinitions.filter(({ value }) =>
            PodRule.CHILD_TYPES.includes(value as number),
          )}
          limitTags={1}
          loading={loadingFilters}
          multiple
          label={translate('programTasksManagement.filter.childType')}
          onChange={(values: Selectable[]) =>
            onChange({
              ...filters,
              childTaskDefinitionId: getFilterItem(
                translate('programTasksManagement.filter.childType'),
                values,
              ),
            })
          }
          size="small"
          value={filters?.childTaskDefinitionId?.value || []}
        />
      </FilterGroup>
      <FilterGroup
        title={translate('components.FilterGroup.ops')}
        count={getFilterCount(pendingFilters, OPS_FILTERS)}
      >
        <Select
          data-cy="program-tasks-modal-engagement-level"
          items={Enum.toSelectable(MemberEngagementLevel.asArray)}
          label={translate('programTasksManagement.filter.engagementLevel')}
          limitTags={1}
          multiple
          onChange={(values: Selectable[]) => {
            setPendingFilters({
              ...pendingFilters,
              engagementLevel: getFilterItem(
                translate('programTasksManagement.filter.engagementLevel'),
                values,
              ),
            });
          }}
          size="small"
          value={pendingFilters.engagementLevel?.value || []}
        />
      </FilterGroup>
    </Panel.FilterModal>
  );
};
